/** Fixed descriptions deliberately exclude map content, URLs and exception details. */
const descriptions = {
  load_started: { level: "info", message: "The map started loading." },
  ready: { level: "info", message: "The map is ready." },
  load_failed: { level: "error", message: "The map could not load." },
  load_timeout: { level: "error", message: "The map load timed out." },
  render_failed: { level: "error", message: "The map could not render." },
  webgl_unavailable: {
    level: "warning",
    message: "WebGL could not start. The map is using SVG.",
  },
  webgl_context_lost: {
    level: "warning",
    message: "The WebGL context was lost. The map is using SVG.",
  },
  webgl_render_failed: {
    level: "warning",
    message: "WebGL could not render. The map is using SVG.",
  },
  routing_worker_unavailable: {
    level: "warning",
    message: "Background routing could not start. The map remains interactive.",
  },
  routing_failed: {
    level: "warning",
    message: "The route could not be calculated. The map remains interactive.",
  },
  routing_timeout: {
    level: "warning",
    message: "The route calculation timed out. The map remains interactive.",
  },
  destroyed: { level: "info", message: "The map instance was destroyed." },
} as const;

export type MapDiagnosticCode = keyof typeof descriptions;
export interface MapDiagnostic {
  readonly code: MapDiagnosticCode;
  readonly level: "info" | "warning" | "error";
  readonly message: string;
  /** Milliseconds since this instance was mounted, rounded to a whole number. */
  readonly elapsedMs: number;
}
export const MAP_DIAGNOSTICS_LIMIT = 50;
export type MapDiagnosticCallback = (diagnostic: MapDiagnostic) => void;

/** Instance-local, bounded history. It never installs listeners or sends telemetry. */
export function createMapDiagnostics(
  onDiagnostic?: MapDiagnosticCallback,
  now: () => number = () => performance.now(),
) {
  const started = now();
  const entries: MapDiagnostic[] = [];
  let callback = onDiagnostic;
  let closed = false;
  return {
    record(code: MapDiagnosticCode) {
      // Runtime allowlisting also guards callers from untyped JavaScript.
      if (closed || !Object.hasOwn(descriptions, code)) return;
      const elapsed = now() - started;
      const entry: MapDiagnostic = Object.freeze({
        code,
        ...descriptions[code],
        elapsedMs: Number.isFinite(elapsed)
          ? Math.max(0, Math.round(elapsed))
          : 0,
      });
      entries.push(entry);
      if (entries.length > MAP_DIAGNOSTICS_LIMIT) entries.shift();
      const notify = callback;
      if (notify) {
        // Observers run after this lifecycle transition, so their own map API
        // calls cannot interrupt an in-progress load or ready announcement.
        queueMicrotask(() => {
          try {
            void Promise.resolve(notify(entry)).catch(() => {});
          } catch {
            // Diagnostic consumers are isolated from the map itself.
          }
        });
      }
    },
    snapshot(): readonly MapDiagnostic[] {
      return entries.map((entry) => ({ ...entry }));
    },
    close() {
      closed = true;
      callback = undefined;
    },
  };
}
